import React, { createContext, use, useEffect, useMemo, useState } from 'react'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import { authPort, userPreferencesPort } from '@/infra/config/beans'
import { getFeatureFlipping } from '@/infra/api/generated/feature-flipping/feature-flipping'

const { findStoreFeatures } = getFeatureFlipping()

// Typing for clarity
export type Flags = Record<string, boolean>

interface FeatureFlagContextType {
  flags: Flags
  isLoading: boolean
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined)

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<Flags>({})
  const [isLoading, setIsLoading] = useState(false)
  const [resolvedAuth, setResolvedAuth] = useState<boolean | null>(null)
  const { store } = useUserPreferences(userPreferencesPort)

  useEffect(() => {
    authPort
      .isAuthenticated()
      .then(setResolvedAuth)
      .catch((error) => {
        console.error('Error while checking authentication', error)
        setResolvedAuth(false)
      })
  }, [])

  useEffect(() => {
    if (!resolvedAuth || !store) {
      void Promise.resolve().then(() => setFlags({}))
      return
    }
    const fetchFlags = async () => {
      try {
        setIsLoading(true)
        const data = await findStoreFeatures(store.id)
        setFlags(data)
      } catch (error) {
        console.error('Error while loading feature flags', error)
      } finally {
        setIsLoading(false)
      }
    }

    void fetchFlags()
  }, [resolvedAuth, store])
  const providerValues = useMemo(() => ({ flags, isLoading }), [flags, isLoading])

  return <FeatureFlagContext value={providerValues}>{children}</FeatureFlagContext>
}

// custom hook to use the feature flags context
export const useFeatureFlags = () => {
  const context = use(FeatureFlagContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider')
  }
  return context
}
